import React, {useEffect, useState} from 'react';
import {Col, Row, Menu, Card, Skeleton, Space, Flex, Typography, Button} from 'antd';
import http from "../framework/http";
import {useNavigate, useParams, Link} from "react-router-dom";
import {observer} from "mobx-react-lite";
import navPathStore from "../store/NavPath.Store.js";
import categoryStore from "../store/Category.Store.js";
import Decimal from "decimal.js";
import useIsMobile from "../framework/MobileCheck";
import {PhoneOutlined} from '@ant-design/icons';
const {Title, Paragraph} = Typography;

function CategoryViewer() {
    const navigate = useNavigate();
    const [categoryTree, setCategoryTree] = useState();
    const [selectedCategories, setSelectedCategories] = useState();
    const [openedCategories, setOpenedCategories] = useState([]);
    const [cardViewContent, setCardViewContent] = useState([]);
    const params = useParams();
    const isMobile = useIsMobile();
    // load categoryTree
    useEffect(() => {
        function buildCategoryTree(categoryMeta, parentId = 0) {
            const categoryTree = [];
            for (const item of categoryMeta.values()) {
                if (item.parentId === parentId) {
                    let categoryNode;
                    if (parentId === 0) {
                        categoryNode = {key: item.id, label: <span className="rootCategoryNode">{item.name}</span>};
                    } else {
                        categoryNode = {key: item.id, label: item.name};
                    }
                    const children = buildCategoryTree(categoryMeta, item.id);
                    if (children.length > 0) {
                        categoryNode.children = children;
                    }
                    categoryTree.push(categoryNode);
                }
            }
            return categoryTree;
        }

        if (categoryStore.getCategory()) {
            setCategoryTree(buildCategoryTree(categoryStore.getCategory()));
        }

    }, [categoryStore.category]);

    // load cards view according to categoryId carried in url
    useEffect(() => {
        if (!categoryStore.getCategory()) {
            return;
        }

        const categoryId = parseInt(params.categoryId);

        if (categoryId === 0) {
            updateTreeMenuState(categoryId);
            loadCategoryCards(categoryId);
            return;
        }

        if (isNaN(categoryId)) {
            navigate("/category/0")
            return;
        }

        const categoryItem = categoryStore.getCategory().get(categoryId)
        if (!categoryItem) {
            navigate("/category/0")
            return;
        }

        let hasChildCategory = false;
        for (const item of categoryStore.getCategory().values()) {
            if (item.parentId === categoryItem.id) {
                hasChildCategory = true;
                break;
            }
        }
        if (hasChildCategory) {
            updateTreeMenuState(categoryId);
            loadCategoryCards(categoryId);
        } else {
            updateTreeMenuState(categoryId);
            loadModelCards(categoryId);
        }

    }, [params, categoryTree]);

    // set tree menu selected keys and opened keys, ensure that there is only one branch open at same time
    const updateTreeMenuState = (categoryId) => {
        let newOpenedCategories = [categoryId.toString()];
        let categoryItem = categoryStore.getCategory().get(categoryId);
        while (categoryItem && categoryItem.parentId !== 0) {
            categoryItem = categoryStore.getCategory().get(categoryItem.parentId);
            newOpenedCategories.push(categoryItem.id.toString());
        }
        setOpenedCategories(newOpenedCategories);
        setSelectedCategories(newOpenedCategories);
        updateNavPath(newOpenedCategories);
        scrollToContentTop();
    };

    const updateNavPath = (keys) => {
        let newNavPath = [];
        keys.map((key) => {
            const categoryItem = categoryStore.getCategory().get(parseInt(key));
            if (categoryItem) {
                newNavPath.unshift({title: <Link to={"/category/" + categoryItem.id}>{categoryItem.name}</Link>})
            }
        });
        newNavPath.unshift({title: <Link to="/" style={{fontWeight: "bold"}}>Home</Link>});
        navPathStore.setNavPath(newNavPath);
    };

    // load category cards view
    const loadCategoryCards = (categoryId) => {
        const filteredByParentId = Array.from((categoryStore.getCategory()).values()).filter((item) => item.parentId === categoryId)
        const newCardContent = [];
        filteredByParentId.forEach((categoryItem) => {
            newCardContent.push(
                <div key={categoryItem.id} className="category-model-card">
                    <Link to={"/category/" + categoryItem.id} className="model-link">
                        <div className="model-card-content">
                            <img
                                src={
                                    categoryItem.picture
                                        ? http.getUri() + categoryItem.picture
                                        : http.getUri() + "images/default.png"
                                }
                                alt={categoryItem.name}
                            />
                            <h3>{categoryItem.name}</h3>
                        </div>
                    </Link>
                </div>
            )
        });

        setCardViewContent(newCardContent);
    };

    // request models by categoryId then load model cards view
    const loadModelCards = async (categoryId) => {
        const res = await http.get("/model/category/" + categoryId);
        if (res.data?.code === 200) {
            const newCardContent = [];
            res.data.data.forEach((modelItem) => {
                newCardContent.push(<Link to={`/category/${params.categoryId}/model/${modelItem.id}`} key={modelItem.id}>
                    <Card className="modelCard" hoverable>
                        <img src={modelItem.cover ?
                            http.getUri() + modelItem.cover :
                            http.getUri() + "images/default.png"} alt=""/>
                        <div className="title">{modelItem.title}</div>
                    </Card>
                </Link>);
            });
            setCardViewContent(newCardContent);
        }
    };

    const scrollToContentTop = () => {
        const headerHeight = 170;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        if (scrollTop > 300) {
            window.scrollTo({
                top: headerHeight,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="category-viewer-container">
            {/* Hero Section */}
            <div className="category-hero">
                <div className="hero-content">
                    <Title level={1}>
                        {params.categoryId && categoryStore.getCategory()?.get(parseInt(params.categoryId))?.name || 'Our Services'}
                    </Title>
                </div>
            </div>

            {/* Models Grid */}
            <div className="models-section">
                <div className="models-grid">
                    {cardViewContent}
                </div>
            </div>
        </div>
    );
}

export default observer(CategoryViewer);
